import { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  FiPlus,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiEdit2,
} from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import ApiServices from "../../services/apiservices";
import { toast } from "sonner";
import { setAdminUsers } from "../../redux/feature/adminuser";
import CustomSpinner from "../../components/customspinner";

export default function AdminUsers() {
  const adminUsers = useSelector((state) => state.adminUser.value);
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [showFormModal, setShowFormModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 5;

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await ApiServices.getRequest("/user/fetch.php");

      if (res.success) {
        dispatch(setAdminUsers(res.allusers));
      }
    } catch (err) {
      console.error(err);
      dispatch(setAdminUsers([]));
    } finally {
      setLoading(false);
    }
  };
  // ================= FILTER =================
  const filteredUsers = adminUsers.filter((user) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      user.firstname?.toLowerCase().includes(searchLower) ||
      user.lastname?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.role?.toLowerCase().includes(searchLower)
    );
  });
  // ================= PAGINATION =================
  const indexOfLast = currentPage * usersPerPage;
  const currentUsers = filteredUsers.slice(
    indexOfLast - usersPerPage,
    indexOfLast
  );
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // ================= VALIDATION =================
  const validationSchema = Yup.object({
    firstname: Yup.string().required("Required"),
    lastname: Yup.string().required("Required"),
    email: Yup.string().email().required("Required"),
    role: Yup.string().required("Required"),
  });

  // ================= CREATE / EDIT =================
  const handleCreateOrEdit = async (values, { setSubmitting, resetForm }) => {
    try {
      if (isEditMode) {
        const payload = { ...values };

        const res = await ApiServices.putRequest(
          `/user/update.php?id=${selectedUser.id}`,
          payload
        );
         if(!res.success){
              toast.error(res.message)
          }else{
              toast.success(res.message);
          }
      } else {
        const res = await ApiServices.postRequest("/user/register.php", values);
         if(!res.success){
            toast.error(res.message)
         }else{
            toast.success(res.message);
          }
      }

      resetForm();
      setShowFormModal(false);
      fetchUsers();
    } catch (err) {
      console.error(err);
      toast.error("Operation failed");
    } finally {
      setSubmitting(false);
    }
  };
  // ================= TOGGLE STATUS =================
  const handleToggleStatus = async () => {
    try {
      const res = await ApiServices.putRequest(
        `/user/togglestatus.php?id=${selectedUser.id}`
      );

      toast.success(res.message);

      setShowStatusModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update status");
    }
  };

  const openEdit = (user) => {
    setSelectedUser(user);
    setIsEditMode(true);
    setShowFormModal(true);
  };

  const openCreate = () => {
    setSelectedUser(null);
    setIsEditMode(false);
    setShowFormModal(true);
  };

  const openStatusModal = (user) => {
    setSelectedUser(user);
    setShowStatusModal(true);
  };
  const handleResetPassword= async (user) => {
    const res = await ApiServices.putRequest(
      `/user/adminresetpassword.php?id=${selectedUser.id}`,
    );
     if(!res.success){
          toast.error(res.message)
      }else{
          toast.success(res.message);
      }
     
  };
  const inputStyle =
  "w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm";

  return (
    <>
    {loading ? <CustomSpinner/> : 
     <main className="flex-1 bg-gray-50 p-6 min-h-screen">
      {/* HEADER */}
      <div className="flex justify-between mb-6">
        <h2 className="text-2xl font-bold">Admin Users</h2>
        <button
          onClick={openCreate}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex gap-2"
        >
          <FiPlus /> Create User
        </button>
      </div>

      {/* SEARCH */}
      <input
        placeholder="Search..."
        className="mb-4 p-2 border rounded w-full max-w-md"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      {/* TABLE */}
      <div className="bg-white rounded shadow">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-4 text-left">Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {currentUsers.length > 0 ? (
            currentUsers.map((user) => (
              <tr key={user.id} className="border-t">
                <td className="p-4">
                  {user.firstname} {user.lastname}
                </td>
                <td>{user.email}</td>
                <td>{user.role}</td>

                {/* STATUS BADGE */}
                <td>
                  <span
                    className={`px-2 py-1 text-xs rounded ${
                      user.deactivate !== "0"
                        ? "bg-red-100 text-red-600"
                        : "bg-green-100 text-green-600"
                    }`}
                  >
                    {user.deactivate === "0" ? "Active" : "Inactive"}
                  </span>
                </td>

                <td className="flex gap-3 p-4">
                  <button onClick={() => openEdit(user)}>
                    <FiEdit2 />
                  </button>

                  <button
                    onClick={() => openStatusModal(user)}
                    className={
                      user.deactivate === "0"
                        ? "text-green-600"
                        : "text-red-600"
                    }
                  >
                    {user.deactivate === "0" ? "Deactivate" : "Activate"}
                  </button>
                </td>
              </tr>
            )) ) : (
                <tr>
                  <td colSpan="4" className="px-6 py-10 text-center text-gray-500">
                    No adminUsers found matching your search.
                  </td>
                </tr>
              )}
          </tbody>
        </table>
      </div>
 {/* Pagination */}
 <div className="flex justify-between items-center mt-6">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((p) => p - 1)}
          className="px-3 py-1 border rounded disabled:opacity-40"
        >
          <FiChevronLeft />
        </button>

        <span className="text-sm">
          Page {currentPage} of {totalPages || 1}
        </span>

        <button
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => setCurrentPage((p) => p + 1)}
          className="px-3 py-1 border rounded disabled:opacity-40"
        >
          <FiChevronRight />
        </button>
      </div>


  {/* CREATE / EDIT MODAL */}
  {showFormModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50 p-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-xl p-6">
            <div className="flex justify-between mb-4">
              <h3 className="font-semibold text-lg">
                {isEditMode ? "Edit User" : "Create User"}
              </h3>
              <button onClick={() => setShowFormModal(false)}>
                <FiX />
              </button>
            </div>

            <Formik
              initialValues={{
                firstname: selectedUser?.firstname || "",
                lastname: selectedUser?.lastname || "",
                email: selectedUser?.email || "",
                role: selectedUser?.role || "",
              }}
              enableReinitialize
              validationSchema={validationSchema}
              onSubmit={handleCreateOrEdit}
            >
              {({ isSubmitting }) => (
                <Form className="space-y-4">
                  <Field name="firstname" placeholder="First Name" className={inputStyle} />
                  <ErrorMessage name="firstname" component="div" className="text-red-500 text-xs" />

                  <Field name="lastname" placeholder="Last Name" className={inputStyle} />
                  <ErrorMessage name="lastname" component="div" className="text-red-500 text-xs" />

                  <Field name="email" type="email" placeholder="Email" className={inputStyle} />
                  <ErrorMessage name="email" component="div" className="text-red-500 text-xs" />

                  <Field as="select" name="role" className={inputStyle}>
                    <option value="">Select Role</option>
                    <option value="superadmin">System Admin</option>
                    <option value="admin">Admin</option>
                    <option value="editor">Editor</option>
                  </Field>
                  <ErrorMessage name="role" component="div" className="text-red-500 text-xs" />
                  {isEditMode && (
                    <>
                      <br/>
                      <button 
                      className="w-full bg-gray-600 text-white py-2 rounded-lg hover:bg-gray-700"                    
                      type="button"
                      onClick={()=>handleResetPassword(selectedUser)}
                      >Click to Reset Password
                      </button>
                      
                    </>
                  )}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                  >
                    {isSubmitting
                      ? "Saving..."
                      : isEditMode
                      ? "Update User"
                      : "Create User"}
                  </button>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}

      {/* STATUS MODAL */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center">
          <div className="bg-white p-6 rounded shadow w-80 text-center">
            <h3 className="text-lg font-semibold mb-3">
              {selectedUser?.deactivate
                ? "Activate User?"
                : "Deactivate User?"}
            </h3>

            <p className="mb-6">
              Are you sure you want to{" "}
              {selectedUser?.deactivate ? "activate" : "deactivate"}{" "}
              {selectedUser?.firstname}?
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowStatusModal(false)}
                className="flex-1 border py-2 rounded"
              >
                Cancel
              </button>

              <button
                onClick={handleToggleStatus}
                className={`flex-1 text-white py-2 rounded ${
                  selectedUser?.deactivate !== "0"
                    ? "bg-green-600"
                    : "bg-red-600"
                }`}
              >
                {selectedUser?.deactivate === "0" ? "Deactivate" : "Activate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>}
    </>
   
  );
}